import { Injectable, RendererFactory2, Renderer2, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ApiService, TranslationService } from '../../../shared/index';
import { ConfigServiceApiService } from 'src/app/shared/api/config-service-api.service';
import { TokenizeGatewayDto } from 'src/app/revenue/shared/gateway-resolution/dto/tokenize-gateway.dto';

declare const Preczn: any;

export interface PrecznClientConfig {
  PublicKey: string;
  MerchantId: string;
  // Echoed back from the Ccgateway row the PublicKey/MerchantId pair was resolved from, so a
  // caller that did not name a gateway still learns which one it was served.
  CcGatewayId?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class PrecznService {
  private readonly _precznApiBase = 'https://api.preczn.com';
  private readonly _clientConfigPath = '/config/v2/PrecznConfig/client-config';
  private _scriptLoaded: Promise<void> | null = null;
  private _renderer: Renderer2;
  // Retained after the load (it used to be discarded) because getAchToken() must pass the public
  // key as its first argument to the Preczn SDK.
  private _clientConfig: PrecznClientConfig | null = null;
  private _scriptElement: HTMLScriptElement | null = null;
  private _loadedCcGatewayId: number | null = null;
  private _loadedMerchantId: string | null = null;
  // The gateway the CURRENTLY IN-FLIGHT load was requested for. Recorded synchronously, where
  // _loadedCcGatewayId is only set in script.onload - without it, a second loadScript() for the
  // SAME gateway arriving before the first completes compares against a still-null
  // _loadedCcGatewayId, reads as a mismatch, and tears the in-flight load down. That is a
  // reachable race, not a hypothetical: a host that resolves its gateway before the widget mounts
  // makes the widget's own ngOnInit load and the ngOnChanges retarget overlap.
  private _requestedCcGatewayId: number | null = null;

  /**
   * The gateway the currently loaded SDK was loaded against. This is what the tokenize sites read
   * to tag the token, so the recorded gateway and the script URL's merchant can never disagree -
   * both are set in the same script.onload handler. Never read it before loadScript() resolves.
   */
  public get currentCcGatewayId(): number | null {
    return this._loadedCcGatewayId;
  }

  public get currentMerchantId(): string | null {
    return this._loadedMerchantId;
  }

  public constructor(
    private _apiService: ApiService,
    private _configApiService: ConfigServiceApiService,
    private _translationService: TranslationService,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this._renderer = rendererFactory.createRenderer(null, null);
  }

  public isPrecznOnly(): Promise<boolean> {
    return this._configApiService.get('/config/v2/PrecznConfig/is-preczn-only');
  }

  /**
   * Answers "which Preczn merchant tokenizes for gateway N" for the direct-gateway surfaces - the
   * Backoffice sites whose selected payment type already names a paying CCGatewayID and therefore
   * never call GatewayResolutionService.resolveForRevenueItems().
   *
   * A null MerchantId in the response IS the answer "gateway N is not a Preczn gateway" (the
   * lookup runs over Preczn rows only, so a CardConnect/AuthNet id matches nothing). That is the
   * specified, non-error response, and it yields null here - never a partial pair.
   *
   * A transient failure of the config call also yields null. The widget's Preczn mode is decided
   * by isPrecznOnly(), not by this call, so a null here leaves the pre-existing behaviour in place
   * (the SDK stays loaded against whatever gateway it was already serving) rather than tearing a
   * working form down over a failed lookup.
   */
  public resolveTokenizeGateway(ccGatewayId: number): Promise<TokenizeGatewayDto | null> {
    if (!(ccGatewayId > 0)) {
      return Promise.resolve(null);
    }
    return this._configApiService
      .get(`${this._clientConfigPath}?ccGatewayId=${ccGatewayId}`)
      .then((config: PrecznClientConfig) =>
        config && config.MerchantId ? ({ ccGatewayId, precznMerchantId: config.MerchantId } as TokenizeGatewayDto) : null
      )
      .catch(() => null);
  }

  /**
   * The single entry point for loading the Preczn PaymentFields SDK.
   *
   * With a gateway pair, the SDK is loaded against that gateway's merchant and the gateway id is
   * recorded. Without one (or with null), the unparameterised client-config request is issued and
   * the gateway id that response echoes is recorded - so there is no path through this service
   * that loads the SDK without recording which gateway it loaded against.
   *
   * When a load is already in flight/complete and a differing gateway is supplied, this delegates
   * to reloadForGateway() rather than returning the memoized promise: a freshly mounted widget
   * must load against ITS gateway, not inherit whatever gateway a previously mounted widget used.
   */
  public loadScript(gateway?: TokenizeGatewayDto | null): Promise<void> {
    if (gateway && !gateway.precznMerchantId) {
      // No merchant for this gateway means it is not a Preczn gateway. Refuse rather than build a
      // merchantId=undefined URL - this single guard is what makes the null-merchant answer safe
      // at every consumer.
      return Promise.reject(new Error('No Preczn merchant is configured for the requested gateway.'));
    }

    if (this._scriptLoaded) {
      if (gateway && gateway.ccGatewayId !== this.currentOrPendingCcGatewayId) {
        return this.reloadForGateway(gateway);
      }
      return this._scriptLoaded;
    }

    const url = gateway ? `${this._clientConfigPath}?ccGatewayId=${gateway.ccGatewayId}` : this._clientConfigPath;
    this._requestedCcGatewayId = gateway?.ccGatewayId ?? null;

    this._scriptLoaded = this._configApiService
      .get(url)
      .then((config: PrecznClientConfig) => {
        this._clientConfig = config;
        const resolvedMerchantId = gateway?.precznMerchantId ?? config?.MerchantId;
        const resolvedCcGatewayId = gateway?.ccGatewayId ?? config?.CcGatewayId ?? null;

        if (!resolvedMerchantId) {
          return Promise.reject(new Error('No Preczn merchant is configured for the requested gateway.'));
        }

        return new Promise<void>((resolve, reject) => {
          const script: HTMLScriptElement = this._renderer.createElement('script');
          this._renderer.setAttribute(
            script,
            'src',
            `${this._precznApiBase}/v1/clients/preczn.min.js?resources=paymentfields&publicApiKey=${config.PublicKey}&merchantId=${resolvedMerchantId}`
          );
          this._renderer.setAttribute(script, 'data-categories', 'essential');
          script.onload = () => {
            this._loadedMerchantId = resolvedMerchantId;
            this._loadedCcGatewayId = resolvedCcGatewayId;
            resolve();
          };
          script.onerror = () => {
            this._scriptLoaded = null;
            this._requestedCcGatewayId = null;
            reject(new Error('Failed to load Preczn PaymentFields SDK.'));
          };
          this._scriptElement = script;
          this._renderer.appendChild(this._document.head, script);
        });
      })
      .catch((err) => {
        this._scriptLoaded = null;
        this._requestedCcGatewayId = null;
        return Promise.reject(err);
      });

    return this._scriptLoaded;
  }

  /**
   * The gateway this service is currently serving - the loaded one, or, while a load is still in
   * flight, the one that load was requested for. The `??` order matters rather than being a
   * straight swap: after loadScript(null) records the gateway the response echoed, a later
   * loadScript({ ccGatewayId: thatSameId }) must be a memoized no-op, not a pointless teardown.
   */
  private get currentOrPendingCcGatewayId(): number | null {
    return this._loadedCcGatewayId ?? this._requestedCcGatewayId;
  }

  /** No-op when the gateway is already loaded or already loading; otherwise tears down and reloads. */
  public async reloadForGateway(gateway: TokenizeGatewayDto): Promise<void> {
    if (gateway && gateway.ccGatewayId === this.currentOrPendingCcGatewayId) {
      // Already loading for this gateway: join that load rather than returning before the SDK is
      // ready, so a caller that flips its widget back into Preczn mode on resolution is right.
      if (this._scriptLoaded) {
        await this._scriptLoaded;
      }
      return;
    }
    this.teardown();
    await this.loadScript(gateway);
  }

  /** Removes the loaded script/global and clears loaded state. Idempotent. */
  public teardown(): void {
    this._scriptLoaded = null;
    this._clientConfig = null;
    this._loadedCcGatewayId = null;
    this._loadedMerchantId = null;
    this._requestedCcGatewayId = null;
    if (this._scriptElement) {
      this._renderer.removeChild(this._document.head, this._scriptElement);
      this._scriptElement = null;
    }
    delete (window as any).Preczn;
  }

  public getToken(): Promise<string> {
    if (typeof Preczn === 'undefined') {
      return Promise.reject(new Error(this._translationService.getInstant('revenue.credit-card-input.tokenize-error')));
    }
    return new Promise<string>((resolve, reject) => {
      Preczn.PaymentFields.Card.getToken((result: any, errors: any) => {
        if (errors) {
          reject(new Error(this.formatPrecznErrors(errors)));
        } else {
          resolve(result.token);
        }
      });
    });
  }

  /**
   * Tokenizes the bank-account fields rendered by the Preczn SDK into the form named by `formId`.
   * This is a TOKENIZATION call - it is not an authorization of any kind, and no amount is sent.
   *
   * Requires the retained _clientConfig: the SDK takes the public key as the first argument here,
   * unlike Card.getToken() which reads it from the loaded script.
   */
  public getAchToken(formId: string): Promise<string> {
    if (typeof Preczn === 'undefined') {
      return Promise.reject(new Error(this._translationService.getInstant('revenue.credit-card-input.tokenize-error')));
    }
    if (!this._clientConfig?.PublicKey) {
      return Promise.reject(new Error(this._translationService.getInstant('revenue.credit-card-input.tokenize-error')));
    }
    const publicKey = this._clientConfig.PublicKey;
    return new Promise<string>((resolve, reject) => {
      Preczn.PaymentFields.getAchToken(publicKey, formId, (result: any) => {
        if (result.errors) {
          reject(new Error(this.formatPrecznErrors(result.errors)));
        } else {
          resolve(result.token);
        }
      });
    });
  }

  // errors from Preczn's getToken/getAchToken callbacks can be a string, an array of strings/objects, or a single object
  // (e.g. a class-validator response: { message: ["number must be...", "cvv must be..."], error: "Bad Request", statusCode: 400 }) -
  // normalize whatever comes back into one readable message instead of letting it stringify to "[object Object]".
  private formatPrecznErrors(errors: any): string {
    if (typeof errors === 'string') {
      return errors;
    }
    if (Array.isArray(errors)) {
      return errors.map((e) => this.formatPrecznErrors(e)).join('\n');
    }
    if (errors?.message) {
      return this.formatPrecznErrors(errors.message);
    }
    return JSON.stringify(errors);
  }
}
